import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { 
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
    Paper, Typography, Box, Chip, IconButton, Dialog, DialogTitle, 
    DialogContent, DialogActions, Button, TextField, Select, MenuItem,
    FormControl, InputLabel, CircularProgress, Tooltip, Grid
} from '@mui/material';
import { Edit as EditIcon, Visibility as ViewIcon } from '@mui/icons-material';
import logo_icon from '../../assets/images/Logo/Bharat-Darshan-Logo.png';
import { api_urls } from "../../utils/api-urls/index.js";
import * as Religious from "../../redux/actions/religiousAction.js";
import axios from 'axios';

const Consultant = () => {
    const dispatch = useDispatch();
    const { consultantData } = useSelector(state => state.religiousReducer);
    const [loading, setLoading] = useState(true);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [editData, setEditData] = useState({ status: '', adminComment: '' });
    const [updateLoading, setUpdateLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        dispatch(Religious.getConsultant());
        setTimeout(() => setLoading(false), 1000);
    }, []);

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-IN', { 
            day: '2-digit', 
            month: 'short', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'approved': return 'success';
            case 'rejected': return 'error';
            case 'contacted': return 'info';
            default: return 'warning';
        }
    };

    const handleView = (item) => {
        setSelectedItem(item);
        setViewDialogOpen(true);
    };

    const handleEdit = (item) => {
        setSelectedItem(item);
        setEditData({ 
            status: item.status || 'pending', 
            adminComment: item.adminComment || '' 
        });
        setEditDialogOpen(true);
    };

    const handleUpdateStatus = async () => {
        if (!selectedItem) return;
        setUpdateLoading(true);
        try {
            await axios.put(`${api_urls}api/admin/update_consultant_status`, {
                id: selectedItem._id,
                status: editData.status,
                adminComment: editData.adminComment
            });
            dispatch(Religious.getConsultant());
            setEditDialogOpen(false);
            alert('Status updated successfully!');
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Failed to update status');
        } finally {
            setUpdateLoading(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
                Consultant Requests
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={3}>
                Total Requests: {consultantData?.length || 0}
            </Typography>

            <TableContainer component={Paper} elevation={3}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#1976d2' }}>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>S.No</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 180 }}>Email</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Phone</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 200 }}>Description</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Category</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date/Time</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!consultantData || consultantData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center">
                                    <Typography color="textSecondary" py={3}>
                                        No consultant requests found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            consultantData.map((row, index) => (
                                <TableRow key={row._id} hover>
                                    <TableCell>{consultantData.length - index}</TableCell>
                                    <TableCell>{row.name || 'N/A'}</TableCell>
                                    <TableCell sx={{ wordBreak: 'break-word', maxWidth: 200 }}>
                                        {row.email || 'N/A'}
                                    </TableCell>
                                    <TableCell>{row.phoneNo || 'N/A'}</TableCell>
                                    <TableCell sx={{ wordBreak: 'break-word', maxWidth: 250 }}>
                                        {row.description || 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                        {row.categoryId?.categoryName || 'N/A'}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {formatDate(row.createdAt)}
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={row.status || 'pending'} 
                                            color={getStatusColor(row.status)} 
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title="View Details">
                                            <IconButton size="small" color="primary" onClick={() => handleView(row)}>
                                                <ViewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Update Status">
                                            <IconButton size="small" color="success" onClick={() => handleEdit(row)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* View Dialog */}
            <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white' }}>
                    Consultant Request Details
                </DialogTitle>
                <DialogContent dividers>
                    {selectedItem && (
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">Name</Typography>
                                <Typography fontWeight="medium">{selectedItem.name || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">Phone</Typography>
                                <Typography fontWeight="medium">{selectedItem.phoneNo || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption" color="textSecondary">Email</Typography>
                                <Typography fontWeight="medium" sx={{ wordBreak: 'break-word' }}>
                                    {selectedItem.email || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">Category</Typography>
                                <Typography fontWeight="medium">
                                    {selectedItem.categoryId?.categoryName || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">Expertise</Typography>
                                <Typography fontWeight="medium">
                                    {selectedItem.expertiseId?.expertiseName || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption" color="textSecondary">Description</Typography>
                                <Typography sx={{ wordBreak: 'break-word', bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                                    {selectedItem.description || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">Status</Typography>
                                <Box>
                                    <Chip 
                                        label={selectedItem.status || 'pending'} 
                                        color={getStatusColor(selectedItem.status)} 
                                        size="small" 
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">Date/Time</Typography>
                                <Typography fontWeight="medium">{formatDate(selectedItem.createdAt)}</Typography>
                            </Grid>
                            {selectedItem.adminComment && (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="textSecondary">Admin Comment</Typography>
                                    <Typography sx={{ bgcolor: '#fff3e0', p: 1, borderRadius: 1 }}>
                                        {selectedItem.adminComment}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewDialogOpen(false)} variant="contained">Close</Button>
                </DialogActions>
            </Dialog>

            {/* Edit Status Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white' }}>
                    Update Status - {selectedItem?.name}
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={editData.status}
                                    label="Status"
                                    onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                                >
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="contacted">Contacted</MenuItem>
                                    <MenuItem value="approved">Approved</MenuItem>
                                    <MenuItem value="rejected">Rejected</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Admin Comment"
                                value={editData.adminComment}
                                onChange={(e) => setEditData({ ...editData, adminComment: e.target.value })}
                                placeholder="Add any notes or comments..."
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)} disabled={updateLoading}>Cancel</Button>
                    <Button 
                        variant="contained" 
                        onClick={handleUpdateStatus} 
                        disabled={updateLoading}
                    >
                        {updateLoading ? <CircularProgress size={24} /> : 'Update'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Consultant;
